/**
 * constants/syncTables.ts
 * Canonical list of tables that participate in cloud sync
 *
 * This module is intentionally minimal to avoid pulling in heavy dependencies
 * when consumers only need the table list (e.g., UI components using softDeleteRecord).
 *
 * IMPORTANT: This file IS included in the sync schema hash check.
 * Adding or removing tables here REQUIRES a SYNC_SCHEMA_VERSION bump
 * because it changes which data is included in sync exports.
 */

/**
 * Table names to include in sync (excludes system catalogs)
 *
 * This is the authoritative list of tables that participate in:
 * - Baseline exports (full snapshots)
 * - Delta exports (incremental changes)
 * - Soft delete via softDeleteRecord()
 *
 * Tables NOT in this list:
 * - sync_state: Device-local sync tracking
 * - sync_settings: Stored in Supabase, syncs via realtime
 * - oauth_tokens: Device-local authentication
 * - directory_handles: Device-local file system access
 * - stored_image_blobs: Device-local binary storage (blobs fetched on-demand per device)
 */
export const SYNC_TABLES = [
  // Core entities
  'accounts',
  'account_risks',
  'balance_histories',
  'sessions',
  'trades',
  'trade_executions',
  'trade_execution_events',
  'trade_opportunities',
  'strategies',
  'playbooks',
  'trading_objectives',
  'economic_releases',
  'market_states',
  'notes',

  // Settings entities
  'basic_settings',
  'pre_trading_settings',
  'live_trading_settings',
  'post_trading_settings',
  'opportunity_settings',
  'trading_objective_settings',
  'market_snapshot_settings',
  'economic_calendar_settings',
  'trading_setup_settings',

  // Reference/lookup entities (user-created, not system catalogs)
  'instruments',
  'brokers',
  'market_condition_categories',
  'playbook_criteria_items',
  'confluences',
  'entry_model_categories',
  'entry_models',
  'fee_verification_records',

  // Asset/media entities (metadata synced, blobs are device-local)
  'stored_images',

  // Transient/draft entities
  'session_drafts',
  'wizard_drafts',
  'note_drafts',
  'retired_draft_lineages',
] as const;

/**
 * Union type of valid sync table names
 *
 * Use this type for functions that accept a sync table name parameter
 * to get compile-time validation.
 *
 * @example
 * function doSomething(tableName: SyncTableName) { ... }
 * doSomething('accounts'); // OK
 * doSomething('invalid'); // Compile error
 */
export type SyncTableName = (typeof SYNC_TABLES)[number];
